import type {
  JobFilterKey,
  JobFilterOption,
  JobFilterOrderItem,
  JobFilters,
  JobFilterState,
  JobMultiFilterKey,
  JobSingleFilterKey,
} from '@/services/jobs/model';
import {
  ALL_JOB_FILTERS,
  JOB_MULTI_FILTER_QUERY_KEYS,
  JOB_SINGLE_FILTER_QUERY_KEYS,
} from '@/services/jobs';

type JobFilterDefinitionBase = {
  isSearch?: boolean;
  label: string;
  optionsKey: keyof JobFilters;
};

export type JobFilterDefinition =
  | (JobFilterDefinitionBase & {
      selectionMode: 'multiple';
      stateKey: JobMultiFilterKey;
    })
  | (JobFilterDefinitionBase & {
      selectionMode: 'single';
      stateKey: JobSingleFilterKey;
    });

export type JobFilterGroup = {
  definition: JobFilterDefinition;
  options: JobFilterOption[];
};

const JOB_FILTER_DEFINITIONS: Record<string, JobFilterDefinition> = {
  postedWithin: {
    label: 'Mới đăng',
    optionsKey: 'postedWithin',
    selectionMode: 'single',
    stateKey: 'postedWithin',
  },
  location: {
    isSearch: true,
    label: 'Địa điểm',
    optionsKey: 'location',
    selectionMode: 'multiple',
    stateKey: 'location',
  },
  salary: {
    label: 'Mức lương',
    optionsKey: 'salaryType',
    selectionMode: 'multiple',
    stateKey: 'salaryType',
  },
  salaryType: {
    label: 'Mức lương',
    optionsKey: 'salaryType',
    selectionMode: 'multiple',
    stateKey: 'salaryType',
  },
  jobLevel: {
    label: 'Cấp bậc',
    optionsKey: 'jobLevel',
    selectionMode: 'multiple',
    stateKey: 'jobLevel',
  },
  type: {
    label: 'Loại công việc',
    optionsKey: 'employmentType',
    selectionMode: 'multiple',
    stateKey: 'employmentType',
  },
  employmentType: {
    label: 'Loại công việc',
    optionsKey: 'employmentType',
    selectionMode: 'multiple',
    stateKey: 'employmentType',
  },
  numOfApplications: {
    label: 'Số ứng viên',
    optionsKey: 'numOfApplications',
    selectionMode: 'single',
    stateKey: 'numOfApplications',
  },
  workingOnWeekend: {
    label: 'Ngày nghỉ',
    optionsKey: 'workingOnWeekend',
    selectionMode: 'multiple',
    stateKey: 'workingOnWeekend',
  },
  workingArrangement: {
    label: 'Hình thức làm việc',
    optionsKey: 'workingArrangement',
    selectionMode: 'multiple',
    stateKey: 'workingArrangement',
  },
};

export function getJobFilterDefinition(
  field: string
): JobFilterDefinition | undefined {
  return JOB_FILTER_DEFINITIONS[field];
}

export function getOrderedJobFilterGroups(
  filterOptions: Partial<JobFilters>,
  filterOrder: JobFilterOrderItem[]
): JobFilterGroup[] {
  const renderedStateKeys = new Set<JobFilterKey>();

  return [...filterOrder]
    .sort((first, second) => first.order - second.order)
    .flatMap(item => {
      const definition = getJobFilterDefinition(item.field);

      if (!definition || renderedStateKeys.has(definition.stateKey)) return [];

      const options = filterOptions[definition.optionsKey];

      if (!options?.length) return [];

      renderedStateKeys.add(definition.stateKey);

      return [{ definition, options }];
    });
}

export function getActiveJobFilterCount(
  filters: JobFilterState,
  includeLocation = false
): number {
  const multiFilterCount = JOB_MULTI_FILTER_QUERY_KEYS.reduce(
    (count, key) =>
      key === 'location' && !includeLocation
        ? count
        : count + filters[key].length,
    0
  );
  const singleFilterCount = JOB_SINGLE_FILTER_QUERY_KEYS.filter(
    key => filters[key] !== ALL_JOB_FILTERS
  ).length;

  return multiFilterCount + singleFilterCount;
}

export function getActiveJobFilterGroupCount(filters: JobFilterState): number {
  return (
    JOB_MULTI_FILTER_QUERY_KEYS.filter(key => filters[key].length > 0).length +
    JOB_SINGLE_FILTER_QUERY_KEYS.filter(key => filters[key] !== ALL_JOB_FILTERS)
      .length
  );
}
